/**
 * **Cetak** — tagihan sebelum bayar, struk sesudah bayar.
 *
 * ### ⛔⛔ Tiga hal yang bisa gagal terpisah, dan ketiganya harus BISA DIBEDAKAN
 * | Gagal di | Yang terjadi | Yang dilihat kasir |
 * |---|---|---|
 * | pencatatan (server) | tak ada apa-apa | galat server apa adanya |
 * | perakitan byte | jejak SUDAH tertulis, byte tak ada | "tercatat, tapi …" + sebabnya |
 * | agen/printer | jejak SUDAH tertulis, byte ada | "tercatat, tapi printer …" |
 *
 * Menyatukan ketiganya jadi "gagal mencetak" adalah kerugian nyata: yang kedua diperbaiki manajer
 * dalam satu menit (isi profil printer di portal), yang ketiga diperbaiki dengan mencolok kabel.
 * Kasir yang tak bisa membedakannya akan memanggil orang yang salah.
 *
 * ### ⚠️ Kenapa tombolnya tak hilang saat agen mati
 * Pencatatan tetap sah dan tetap berguna — dan lagi, printer `net` tak selalu butuh agen. Tombol
 * yang hilang membuat kasir mengira fiturnya tak ada; tombol yang menolak dengan alasan membuatnya
 * tahu apa yang harus diperbaiki.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { printViaAgent } from '../api/agent';
import { api } from '../api/client';
import { staffSession } from '../api/session';

export interface PrintButtonProps {
  readonly orderId: string;
  readonly docType: 'bill' | 'receipt';
  readonly deviceCode: string;
}

type Status =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'ok'; count?: number }
  /** ⭐ `recorded` = jejak tertulis, kertas tidak. Sengaja BEDA dari `error`. */
  | { kind: 'recorded'; message: string }
  | { kind: 'error'; message: string };

export function PrintButton({ orderId, docType, deviceCode }: PrintButtonProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const cetak = async () => {
    setStatus({ kind: 'busy' });
    const s = staffSession();
    let hasil;
    try {
      hasil = await api.print(orderId, {
        docType,
        deviceCode,
        actor: { actorKind: 'staff', code: s?.staffCode ?? '', name: s?.displayName ?? '' },
      });
    } catch (e: unknown) {
      setStatus({ kind: 'error', message: (e as Error).message });
      return;
    }

    /*
     * ⛔ Sampai sini pencetakan SUDAH tercatat di server. Apa pun yang gagal sesudahnya bukan lagi
     * "gagal mencetak" — jejaknya ada, dan kasir perlu tahu itu supaya tak menekan tombolnya
     * berkali-kali (tiap ketukan menaikkan `printCount` untuk kertas yang tak pernah keluar).
     */
    if (hasil.job === undefined) {
      setStatus({
        kind: 'recorded',
        message: hasil.printError?.message ?? t('cashier.print.noJob'),
      });
      return;
    }

    const kirim = await printViaAgent({
      bytesBase64: hasil.job.bytesBase64,
      address: hasil.job.address,
    });
    if (kirim.ok) {
      setStatus({ kind: 'ok', ...(hasil.printCount === undefined ? {} : { count: hasil.printCount }) });
      return;
    }
    setStatus({
      kind: 'recorded',
      message: `${kirim.code ?? ''} ${kirim.message ?? ''}`.trim(),
    });
  };

  return (
    <div className="print-box">
      <button
        type="button"
        data-testid={`print-${docType}`}
        disabled={status.kind === 'busy'}
        onClick={() => void cetak()}
      >
        {t(docType === 'bill' ? 'cashier.print.bill' : 'cashier.print.receipt')}
      </button>

      {status.kind === 'ok' && (
        <p className="ok" data-testid="print-ok">
          {t('cashier.print.sent')}
          {status.count !== undefined && status.count > 1
            ? ` (${t('cashier.print.copyNo', { n: status.count })})`
            : ''}
        </p>
      )}

      {/*
        ⚠️ Dibedakan dari galat biasa DI LAYAR, bukan cuma di kode: warnanya peringatan, dan
        kalimatnya dibuka dengan "tercatat". Kasir yang membacanya tahu bahwa menekan lagi tak akan
        menolong — yang perlu dibetulkan ada di luar layar ini.
      */}
      {status.kind === 'recorded' && (
        <p className="warn" data-testid="print-recorded">
          {t('cashier.print.recordedNotPrinted')} {status.message}
        </p>
      )}

      {status.kind === 'error' && (
        <p className="error" data-testid="print-error">{status.message}</p>
      )}
    </div>
  );
}
